import type { CaptureSequence } from '@/components/posture-analysis/types';

/**
 * Remote Assessment -> Posture Analysis handoff between two tabs of the same app. The workbench
 * tab keeps the stills in memory and answers requests on a BroadcastChannel; nothing is written
 * to localStorage / sessionStorage (client images are health data).
 */
export const POSTURE_HANDOFF_CHANNEL = 'harry-remote-posture-handoff';

export interface PostureHandoffPayload {
  clientId: string;
  views: Partial<Record<CaptureSequence, string>>;
}

type Msg =
  | { type: 'request'; id: string }
  | { type: 'payload'; id: string; payload: PostureHandoffPayload };

type ChannelCtor = new (name: string) => {
  onmessage: ((e: MessageEvent) => void) | null;
  postMessage: (data: unknown) => void;
  close: () => void;
};

const defaultChannel = (): ChannelCtor | undefined =>
  typeof BroadcastChannel === 'undefined' ? undefined : (BroadcastChannel as unknown as ChannelCtor);

/** Workbench side: answer requests for `id` with the current stills. Returns a stop function. */
export function servePostureHandoff(
  id: string,
  getPayload: () => PostureHandoffPayload,
  Channel: ChannelCtor | undefined = defaultChannel(),
): () => void {
  if (!Channel) return () => undefined;
  const channel = new Channel(POSTURE_HANDOFF_CHANNEL);
  channel.onmessage = (e) => {
    const msg = e.data as Msg;
    if (msg?.type === 'request' && msg.id === id) {
      channel.postMessage({ type: 'payload', id, payload: getPayload() } satisfies Msg);
    }
  };
  return () => channel.close();
}

/** Posture Analysis side: ask the workbench tab for the stills; null if nobody answers in time. */
export function requestPostureHandoff(
  id: string,
  { timeoutMs = 3000, Channel = defaultChannel() }: { timeoutMs?: number; Channel?: ChannelCtor } = {},
): Promise<PostureHandoffPayload | null> {
  if (!Channel) return Promise.resolve(null);
  return new Promise((resolve) => {
    const channel = new Channel(POSTURE_HANDOFF_CHANNEL);
    const done = (value: PostureHandoffPayload | null) => {
      clearTimeout(timer);
      channel.close();
      resolve(value);
    };
    const timer = setTimeout(() => done(null), timeoutMs);
    channel.onmessage = (e) => {
      const msg = e.data as Msg;
      if (msg?.type === 'payload' && msg.id === id) done(msg.payload);
    };
    channel.postMessage({ type: 'request', id } satisfies Msg);
  });
}
